(() => {
  const instances = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  function initialize(root = document) {
    root.querySelectorAll('[data-collection-section].featured-rail').forEach(section => {
      if (instances.has(section) || section.dataset.autoScroll !== 'true') return;
      const row = section.querySelector('.product-grid');
      const button = section.querySelector('[data-rail-pause]');
      if (!row || !button) return;
      const abort = new AbortController();
      const options = {signal: abort.signal};
      let visible = false, hover = false, focused = false, paused = false, held = false;
      let resumeAt = 0, direction = 1, frame = 0, last = 0, position = row.scrollLeft;
      const speed = Math.max(5, Math.min(40, Number(section.dataset.scrollSpeed) || 15));
      const hasOverflow = () => row.scrollWidth - row.clientWidth > 1;
      function allowed() {
        return visible && !document.hidden && !reduced.matches && !hover && !focused && !paused && !held && hasOverflow();
      }
      function stop() { cancelAnimationFrame(frame); frame = 0; last = 0; }
      function refresh() {
        button.hidden = reduced.matches || !hasOverflow();
        row.classList.toggle('rail-moving', !reduced.matches);
        if (!allowed()) { stop(); return; }
        if (!frame) { position = row.scrollLeft; frame = requestAnimationFrame(tick); }
      }
      function tick(now) {
        frame = 0;
        if (!allowed()) { last = 0; return; }
        const elapsed = last ? Math.min(now - last, 50) / 1000 : 0;
        last = now;
        if (now >= resumeAt) {
          const end = row.scrollWidth - row.clientWidth;
          position = Math.max(0, Math.min(end, position + direction * speed * elapsed));
          row.scrollLeft = position;
          if ((direction > 0 && position >= end) || (direction < 0 && position <= 0)) {
            direction = position >= end ? -1 : 1;
            resumeAt = now + 1200;
          }
        } else { position = row.scrollLeft; }
        frame = requestAnimationFrame(tick);
      }
      function manual() { resumeAt = performance.now() + 4000; position = row.scrollLeft; }
      section.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { hover = true; refresh(); } }, options);
      section.addEventListener('pointerleave', () => { hover = false; refresh(); }, options);
      section.addEventListener('focusin', () => { focused = true; refresh(); }, options);
      section.addEventListener('focusout', event => { focused = section.contains(event.relatedTarget); refresh(); }, options);
      row.addEventListener('pointerdown', () => { held = true; manual(); refresh(); }, options);
      const release = () => { if (held) { held = false; manual(); refresh(); } };
      document.addEventListener('pointerup', release, options);
      document.addEventListener('pointercancel', release, options);
      row.addEventListener('touchstart', () => { held = true; manual(); refresh(); }, {...options, passive: true});
      row.addEventListener('touchend', release, options);
      row.addEventListener('touchcancel', release, options);
      row.addEventListener('pointermove', event => { if (event.buttons || event.pointerType === 'touch') manual(); }, options);
      row.addEventListener('wheel', event => { if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey) manual(); }, {...options, passive: true});
      row.addEventListener('keydown', manual, options);
      button.addEventListener('click', () => {
        paused = !paused;
        button.setAttribute('aria-pressed', String(paused));
        button.setAttribute('aria-label', paused ? 'Resume automatic product scrolling' : 'Pause automatic product scrolling');
        button.textContent = paused ? 'Resume motion' : 'Pause motion';
        refresh();
      }, options);
      document.addEventListener('visibilitychange', refresh, options);
      window.addEventListener('pageshow', () => { held = false; resumeAt = 0; refresh(); }, options);
      reduced.addEventListener('change', refresh, options);
      const intersection = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        refresh();
      }, {threshold: 0, rootMargin: '120px 0px'});
      intersection.observe(row);
      const resize = new ResizeObserver(refresh);
      resize.observe(row);
      instances.set(section, () => { stop(); abort.abort(); intersection.disconnect(); resize.disconnect(); });
      refresh();
    });
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [section, cleanup] of instances) {
      if (event.target.contains(section)) { cleanup(); instances.delete(section); }
    }
  });
  initialize();
})();
/* Product-card photo browsing; horizontal drags do not navigate. */
(() => {
  const initialized = new WeakSet();
  const style = document.createElement('style');
  style.textContent = '.card-image[data-photo-swipe]{touch-action:pan-y}.card-photo-view{position:absolute!important;inset:0;width:100%;height:100%;object-fit:var(--card-fit,contain);z-index:1;pointer-events:none}.card-image.wp-spin-host .card-photo-view{display:none}.card-photo-count{position:absolute;bottom:5px;left:50%;transform:translateX(-50%);z-index:3;display:flex;gap:3px;pointer-events:none}.card-photo-count i{display:block;width:3px;height:3px;border-radius:50%;background:#8886}.card-photo-count i.is-current{background:#3339}.card-image.wp-spin-host .card-photo-count{display:none}.card-image[data-photo-swipe] .badge{z-index:4}';
  document.head.append(style);
  function scan(root = document) {
    const cards = [...(root.matches?.('.product-card') ? [root] : []), ...root.querySelectorAll?.('.product-card') || []];
    for (const card of cards) {
      if (initialized.has(card)) continue;
      const data = card.querySelector('[data-card-photos]'), link = card.querySelector('.card-image');
      if (!data || !link) continue;
      let photos; try { photos = JSON.parse(data.textContent); } catch { continue; }
      if (photos.length < 2) continue;
      initialized.add(card); link.dataset.photoSwipe = 'true';
      const count = document.createElement('span'); count.className = 'card-photo-count'; count.setAttribute('aria-hidden','true'); const dots = photos.map((_, i) => { const dot = document.createElement('i'); dot.classList.toggle('is-current', i === 0); count.append(dot); return dot; }); link.append(count);
      let index = 0, start = null, moved = false, suppressUntil = 0, picture;
      const active = () => link.classList.contains('wp-spin-host');
      function show(direction) {
        index = (index + direction + photos.length) % photos.length;
        if (!picture) { picture = document.createElement('img'); picture.className = 'card-photo-view'; picture.alt = link.getAttribute('aria-label') || ''; picture.draggable = false; link.append(picture); }
        picture.src = photos[index]; dots.forEach((dot, i) => dot.classList.toggle('is-current', i === index));
      }
      link.addEventListener('pointerdown', event => {
        if (active() || event.button !== 0) return;
        start = {x:event.clientX,y:event.clientY,id:event.pointerId}; moved = false;
      });
      link.addEventListener('pointermove', event => {
        if (!start || active()) return;
        const dx = event.clientX-start.x, dy = event.clientY-start.y;
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { start = null; return; }
        if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
          moved = true; event.preventDefault(); event.stopPropagation();
          if (!link.hasPointerCapture(event.pointerId)) link.setPointerCapture(event.pointerId);
        }
      });
      link.addEventListener('pointerup', event => {
        if (!start) return;
        if (moved) { suppressUntil = Date.now()+700; if (Math.abs(event.clientX-start.x)>30) show(event.clientX<start.x?1:-1); }
        start = null;
      });
      link.addEventListener('pointercancel', () => { start = null; });
      link.addEventListener('dragstart', event => { if (!active()) event.preventDefault(); });
      link.addEventListener('click', event => { if (Date.now()<suppressUntil) {event.preventDefault();event.stopImmediatePropagation();} }, true);
      link.addEventListener('keydown', event => { if (!active() && ['ArrowLeft','ArrowRight'].includes(event.key)) {event.preventDefault();show(event.key==='ArrowRight'?1:-1);} });
    }
  }
  scan();
  new MutationObserver(records => { for (const record of records) for (const node of record.addedNodes) if (node.nodeType===1) scan(node); }).observe(document.body,{childList:true,subtree:true});
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
